import { useNavigate, useParams } from "react-router-dom";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import FormButton from "components/organisms/form/FormButton";
import { useGetIntercomSegmentQuery, useUpsertIntercomSegmentMutation } from "pages/Configuration/IntercomSegments/IntercomSegments.graphql-gen";
import { useSegmentsQuery } from "pages/Content/FeedCards/Content.graphql-gen";
import { featureFlagLabelToGroup } from "pages/Content/FeedCards/CreateContentPage";
import { capitalizeFirstLetter } from "utils/Utils";

type SelectOption = {
	label?: string;
	value?: string;
	group?: string;
};

export default function EditIntercomSegment() {
	const { intercomSegmentId } = useParams<{ intercomSegmentId: string }>();

	const navigate = useNavigate();

	const [upsertIntercomSegment] = useUpsertIntercomSegmentMutation();

	const { data, error } = useGetIntercomSegmentQuery({
		variables: {
			id: intercomSegmentId ?? "",
		},
	});

	const { data: segmentsData } = useSegmentsQuery();

	const options =
		segmentsData?.segments?.map((s) => {
			return {
				...s,
				group: featureFlagLabelToGroup(s.label ?? ""),
			};
		}) ?? [];

	const map = new Map<string, SelectOption[]>();

	options?.forEach((o) => {
		const groupName = o.group || "Ukategorisert";
		if (!map.has(groupName)) {
			map.set(groupName, []);
		}

		map.get(groupName)?.push(o);
	});

	if (!data?.singleLabelSegment) return <>Loading...</>;
	return (
		<div>
			<MainLayoutPageHeader title={data?.singleLabelSegment?.label} />
			<Flex gap="16px" style={{ marginTop: "24px" }}>
				<form
					onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
						e.preventDefault();
						const form = e.currentTarget;
						const segmentInSelect = form.elements.namedItem("segmentIn") as HTMLSelectElement;
						const segmentNotInSelect = form.elements.namedItem("segmentNotIn") as HTMLSelectElement;

						const SegmentIn: string[] = Array.from(segmentInSelect.options)
							.filter((option: HTMLOptionElement) => option.selected)
							.map((option: HTMLOptionElement) => option.value);

						const SegmentNotIn: string[] = Array.from(segmentNotInSelect.options)
							.filter((option: HTMLOptionElement) => option.selected)
							.map((option: HTMLOptionElement) => option.value);

						const response = await upsertIntercomSegment({
							variables: {
								labelSegment: {
									id: data?.singleLabelSegment?.id,
									label: data?.singleLabelSegment?.label,
									segmentIn: SegmentIn,
									segmentNotIn: SegmentNotIn,
								},
							},
						});

						if (response.data?.upsertLabelSegment?.id) {
							navigate("/intercom-segments");
						}
					}}
				>
					<Flex gap="16px" direction={Direction.column}>
						<Flex gap="16px">
							<select name="segmentIn" multiple defaultValue={data?.singleLabelSegment?.segmentIn ?? []} key={data?.singleLabelSegment?.id}>
								{
									<>
										{Array.from(map.keys()).map((key) => {
											return (
												<optgroup key={key} label={capitalizeFirstLetter(key.replace("_", " "))}>
													{map.get(key)?.map((o, index) => (
														<option key={index + "a"} label={o.label} value={o.value}>
															{o.value}
														</option>
													))}
												</optgroup>
											);
										})}
									</>
								}
							</select>
						</Flex>
						<Flex gap="16px">
							<select name="segmentNotIn" multiple defaultValue={data?.singleLabelSegment?.segmentNotIn ?? []} key={data?.singleLabelSegment?.id}>
								{
									<>
										{Array.from(map.keys()).map((key) => {
											return (
												<optgroup key={key} label={capitalizeFirstLetter(key.replace("_", " "))}>
													{map.get(key)?.map((o, index) => (
														<option key={index + "a"} label={o.label} value={o.value}>
															{o.value}
														</option>
													))}
												</optgroup>
											);
										})}
									</>
								}
							</select>
						</Flex>
					</Flex>

					{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
					<FormButton style={{ marginTop: "24px" }} label="Oppdater intercom segment" />
				</form>
			</Flex>
		</div>
	);
}
